import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Icons, useTheme } from '@ublend-npm/aulaui-core';
import { getCanSwitchHomeViewRole } from '@app/selectors/home';
import EducatorStudentToggle from '../../EducatorStudentToggle';
import { getSpaceWidgetColors } from './spaceWidgetColors';

type SpaceWidgetToolbarProps = {
  canScrollBack: boolean;
  canScrollForward: boolean;
  onScrollBack: () => void;
  onScrollForward: () => void;
  showNavigation?: boolean;
  showRoleToggle?: boolean;
};

/** Matches My Spaces `ScrollBox` recently-viewed arrow style. */
const getNavArrowSx = (enabled: boolean, color: string) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color,
  cursor: enabled ? 'pointer' : 'not-allowed',
  opacity: enabled ? 1 : 0.3,
  transition: 'opacity 0.2s',
  outline: 'none',
  '&:hover': enabled ? { opacity: 0.7 } : {},
  '&:focus': { outline: 'none' },
  '&:focus-visible': enabled ? { opacity: 0.7 } : {},
});

const SpaceWidgetToolbar = ({
  canScrollBack,
  canScrollForward,
  onScrollBack,
  onScrollForward,
  showNavigation = true,
  showRoleToggle = true,
}: SpaceWidgetToolbarProps) => {
  const { palette } = useTheme();
  const colors = getSpaceWidgetColors(palette);
  const canSwitchRole = useSelector(getCanSwitchHomeViewRole);
  const showRoleControls = showRoleToggle && canSwitchRole;

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={2}
      width="100%"
    >
      <Box display="flex" alignItems="center" gap={1} minWidth={0}>
        {showRoleControls ? <EducatorStudentToggle /> : null}
      </Box>

      {showNavigation ? (
        <Box display="flex" alignItems="center" gap={2} flexShrink={0}>
          <Box
            role="button"
            tabIndex={canScrollBack ? 0 : -1}
            aria-label="Scroll spaces left"
            aria-disabled={!canScrollBack}
            onClick={canScrollBack ? onScrollBack : undefined}
            onKeyDown={(event) => {
              if (!canScrollBack) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onScrollBack();
              }
            }}
            sx={getNavArrowSx(canScrollBack, colors.navArrowIcon)}
          >
            <Icons.ArrowBack />
          </Box>
          <Box
            role="button"
            tabIndex={canScrollForward ? 0 : -1}
            aria-label="Scroll spaces right"
            aria-disabled={!canScrollForward}
            onClick={canScrollForward ? onScrollForward : undefined}
            onKeyDown={(event) => {
              if (!canScrollForward) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onScrollForward();
              }
            }}
            sx={getNavArrowSx(canScrollForward, colors.navArrowIcon)}
          >
            <Icons.ArrowForward />
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default SpaceWidgetToolbar;
